import type { BuildTimelineResult, HeaterType } from '../../engines/types';
import { getT } from '../../i18n';
import type { Market } from '../location/types';

interface EstimateBuildTimeInput {
  areaSqm: number;
  heaterType: HeaterType;
  includesShower: boolean;
  hasRelaxRoom?: boolean;
  market?: Market;
  /**
   * Facade treatment work, when a colour was chosen (issue 134). Absent for an
   * untreated facade, which is the default — so the default timeline is
   * unchanged.
   *
   * `applicationHours` is already derived from the real facade area, so this
   * step is exempt from `areaFactor`; scaling it again would bill the area
   * twice.
   */
  exteriorTreatment?: {
    applicationHours: number;
    dryingHours: number;
  };
}

interface LeadTimeRange {
  min: number;
  max: number;
}

const ELECTRIC_LEAD_TIME_WEEKS: LeadTimeRange = { min: 2, max: 4 };
const FIREPLACE_LEAD_TIME_WEEKS: LeadTimeRange = { min: 2, max: 6 };
const PLUMBING_LEAD_TIME_WEEKS: LeadTimeRange = { min: 1, max: 2 };

function formatTemplate(
  template: string,
  args: Record<string, string | number>,
): string {
  return Object.entries(args).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

function getWeekendRange(totalHours: number): LeadTimeRange {
  const min = Math.max(1, Math.ceil(totalHours / 18));
  const max = Math.max(min, Math.ceil(totalHours / 12));
  return { min, max };
}

function sumLeadTimes(ranges: Array<LeadTimeRange | undefined>): LeadTimeRange | undefined {
  const defined = ranges.filter((range): range is LeadTimeRange => Boolean(range));
  if (defined.length === 0) return undefined;
  return defined.reduce(
    (total, range) => ({
      min: total.min + range.min,
      max: total.max + range.max,
    }),
    { min: 0, max: 0 },
  );
}

function formatRangeLabel(
  range: LeadTimeRange,
  templates: {
    single: string;
    plural: string;
    between: string;
  },
): string {
  if (range.min === range.max) {
    return formatTemplate(
      range.min === 1 ? templates.single : templates.plural,
      { count: range.min },
    );
  }

  return formatTemplate(templates.between, {
    min: range.min,
    max: range.max,
  });
}

export function estimateBuildTime({
  areaSqm,
  heaterType,
  includesShower,
  // Only the two-room engine passes this flag; a square/barrel sauna has no
  // partition wall, so the default must be false — defaulting to true billed
  // every single-room sauna ~2-4 h of phantom partition framing.
  hasRelaxRoom = false,
  market = 'se',
  exteriorTreatment,
}: EstimateBuildTimeInput): BuildTimelineResult {
  const t = getT(market);
  const areaFactor = Math.min(1.8, Math.max(0.8, areaSqm / 5.5));
  const partitionWallHours = hasRelaxRoom ? 2 : 0;
  interface BaseStep {
    id: string;
    label: string;
    hours: number;
    leadTimeWeeks?: LeadTimeRange;
    weatherCritical?: boolean;
    weatherCriticalReason?: string;
    /** Skip the floor-area scaling — the hours are already area-derived. */
    fixedHours?: boolean;
    dryingHours?: number;
  }
  const baseSteps: BaseStep[] = [
    { id: 'foundation', label: t.domain_build_foundationTitle, hours: 8 },
    ...(includesShower
      ? [{
          id: 'drainage',
          label: t.domain_build_drainageTitle,
          hours: 4,
          leadTimeWeeks: PLUMBING_LEAD_TIME_WEEKS,
        }]
      : []),
    { id: 'framing', label: t.domain_build_framingTitle, hours: 7 + partitionWallHours },
    {
      id: 'roof',
      label: t.domain_build_roofTitle,
      hours: 8,
      weatherCritical: true,
      weatherCriticalReason: t.domain_build_timeline_weatherCriticalReason,
    },
    { id: 'facade', label: t.domain_build_facadeTitle, hours: 6 },
    ...(exteriorTreatment
      ? [{
          id: 'facade-treatment',
          label: t.domain_build_facadeTreatmentTitle,
          hours: exteriorTreatment.applicationHours,
          fixedHours: true,
          weatherCritical: true,
          weatherCriticalReason: t.domain_build_timeline_treatmentWeatherReason,
          dryingHours: exteriorTreatment.dryingHours,
        }]
      : []),
    { id: 'insulation', label: t.domain_build_insulationTitle, hours: 8 },
    { id: 'benches', label: t.domain_build_benchesTitle, hours: 5 },
    {
      id: 'systems',
      label: t.domain_build_systemsTitle,
      hours: heaterType === 'electric' ? 7 : 5,
      leadTimeWeeks: heaterType === 'electric' ? ELECTRIC_LEAD_TIME_WEEKS : undefined,
    },
    ...(heaterType === 'wood'
      ? [{
          id: 'chimney',
          label: t.domain_build_chimneyTitle,
          hours: 4,
          leadTimeWeeks: FIREPLACE_LEAD_TIME_WEEKS,
        }]
      : []),
    { id: 'handover', label: t.domain_build_handoverTitle, hours: 2 },
  ];

  const steps = baseSteps.map((step) => {
    // `fixedHours` steps already carry a real, measured figure (the facade
    // treatment's hours come from the actual painted area), so scaling them by
    // the floor-area factor would count the size of the building twice.
    const isFixed = step.id === 'handover' || step.fixedHours === true;
    const hours = isFixed ? step.hours : Math.round(step.hours * areaFactor);
    const weekendSessions = Math.max(1, Math.ceil(hours / 8));

    return {
      id: step.id,
      label: step.label,
      hours,
      weekendLabel: formatTemplate(
        weekendSessions === 1
          ? t.domain_build_timeline_weekendPassSingle
          : t.domain_build_timeline_weekendPassPlural,
        { count: weekendSessions },
      ),
      leadTimeWeeks: step.leadTimeWeeks,
      weatherCritical: step.weatherCritical,
      weatherCriticalReason: step.weatherCriticalReason,
      // Spread rather than assign: an explicit `dryingHours: undefined` on
      // every step would show up in every timeline snapshot for the benefit of
      // the one step that ever has a value.
      ...(step.dryingHours === undefined ? {} : { dryingHours: step.dryingHours }),
    };
  });

  const totalHours = steps.reduce((sum, step) => sum + step.hours, 0);
  const weekendRange = getWeekendRange(totalHours);
  const totalLeadTimeWeeks = sumLeadTimes(steps.map((step) => step.leadTimeWeeks));
  const weekendRangeLabel = formatRangeLabel(weekendRange, {
    single: t.domain_build_timeline_weekendRangeSingle,
    plural: t.domain_build_timeline_weekendRangePlural,
    between: t.domain_build_timeline_weekendRangeBetween,
  });
  const leadTimeRangeLabel = totalLeadTimeWeeks
    ? formatRangeLabel(totalLeadTimeWeeks, {
        single: t.domain_build_timeline_leadRangeSingle,
        plural: t.domain_build_timeline_leadRangePlural,
        between: t.domain_build_timeline_leadRangeBetween,
      })
    : undefined;

  return {
    totalHours,
    // Derived from the SAME 12-18 h/weekend assumption as the calendar range
    // label; ceil(hours/8) rendered "~8 helger" next to "≈ 4-6 helger".
    weekendEstimate: Math.max(
      1,
      Math.round((weekendRange.min + weekendRange.max) / 2),
    ),
    totalLeadTimeWeeks,
    calendarEstimateLabel: leadTimeRangeLabel
      ? formatTemplate(t.domain_build_timeline_calendarEstimateWithLead, {
          hours: totalHours,
          weekendRange: weekendRangeLabel,
          leadRange: leadTimeRangeLabel,
        })
      : formatTemplate(t.domain_build_timeline_calendarEstimateNoLead, {
          hours: totalHours,
          weekendRange: weekendRangeLabel,
        }),
    steps,
  };
}
